/**
 * Command registry for the terminal shell.
 *
 * Each command's `run` returns a ReactNode that gets appended to the screen
 * (or `null` for no output). Commands reach shell state — cwd, theme, the
 * screen — through the injected CommandContext. Content is read from
 * src/content.ts; this file owns only presentation + shell behaviour.
 */

import { translate, type OsLang } from "../os/i18n-core";
import type { ReactNode } from "react";
import { content } from "../content";
import { contentFor } from "../content.i18n";
import {
  dirAt,
  pathString,
  resolvePath,
  slugify,
  type FsNode,
} from "./filesystem";
import { setSoundEnabled, soundEnabled } from "./sound";
import s from "./Terminal.module.css";

/** SliceOS wants a desk-sized display; phones get a lore-flavored refusal. */
function displayTooSmallForOs(): boolean {
  return window.innerWidth < 768;
}

export const THEMES = ["green", "amber", "blue", "vcr"] as const;
export type ThemeName = (typeof THEMES)[number];

export interface CommandContext {
  /** OS display language for shell chrome (content stays English). */
  lang: OsLang;
  cwd: string[];
  setCwd: (path: string[]) => void;
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  clear: () => void;
  history: string[];
  /** Ends the session (CRT off → SliceOS, or closes the in-OS window). */
  exit: (() => void) | null;
  /** Whole page ("fullscreen") or a window inside SliceOS ("windowed"). */
  variant: "fullscreen" | "windowed";
}

export interface Command {
  description: string;
  usage?: string;
  aliases?: string[];
  /** "core" = the portfolio commands shown first in help; rest are builtins. */
  group?: "core" | "system";
  /** Hidden commands don't show in `help` or tab-completion suggestions. */
  hidden?: boolean;
  run: (args: string[], ctx: CommandContext) => ReactNode;
}

/* ------------------------------------------------------------------ */
/* small presentational helpers                                        */
/* ------------------------------------------------------------------ */

function Heading({ children }: { children: ReactNode }) {
  return <div className={s.head}>{children}</div>;
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <div className={s.bullet}>
      <span className={s.accent}>›</span>
      <span>{children}</span>
    </div>
  );
}

function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a className={s.link} href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  );
}

/* ------------------------------------------------------------------ */
/* renderers shared between top-level commands and `cat`               */
/* ------------------------------------------------------------------ */

function renderAbout(lang: OsLang = "en"): ReactNode {
  const C = contentFor(lang);
  return (
    <div className={s.block}>
      <Heading>{translate(lang, "// about")}</Heading>
      {C.about.map((para, i) => (
        <p key={i} className={s.para}>
          {para}
        </p>
      ))}
    </div>
  );
}

function renderJob(job: (typeof content.experience)[number]): ReactNode {
  return (
    <div className={s.entry} key={job.company}>
      <div className={s.entryHead}>
        <span className={s.bright}>{job.role}</span>
        <span className={s.dim}> @ </span>
        <span className={s.accent}>{job.company}</span>
      </div>
      <div className={s.meta}>
        {job.period}
        {job.location ? ` · ${job.location}` : ""}
      </div>
      {job.summary ? <p className={s.para}>{job.summary}</p> : null}
      {job.highlights.map((h, i) => (
        <Bullet key={i}>{h}</Bullet>
      ))}
    </div>
  );
}

function renderExperience(args: string[], lang: OsLang = "en"): ReactNode {
  const C = contentFor(lang);
  const slug = args[0]?.replace(/\.txt$/, "");
  const jobs = slug
    ? C.experience.filter((j) => slugify(j.company) === slug)
    : C.experience;

  if (slug && jobs.length === 0) {
    return (
      <div className={s.block}>
        <span className={s.err}>no such role: {slug}</span>
      </div>
    );
  }

  return (
    <div className={s.block}>
      <Heading>{translate(lang, "// experience")}</Heading>
      {jobs.map(renderJob)}
    </div>
  );
}

function renderProject(proj: (typeof content.projects)[number]): ReactNode {
  return (
    <div className={s.entry} key={proj.name}>
      <div className={s.entryHead}>
        <span className={proj.featured ? s.accent : s.bright}>
          {proj.featured ? "★ " : ""}
          {proj.name}
        </span>
        {proj.role ? <span className={s.dim}> — {proj.role}</span> : null}
      </div>
      <p className={s.para}>{proj.blurb}</p>
      {proj.tags && proj.tags.length > 0 ? (
        <div className={s.tags}>
          {proj.tags.map((t) => (
            <span key={t} className={s.tag}>
              {t}
            </span>
          ))}
        </div>
      ) : null}
      {proj.link ? (
        <div className={s.para}>
          <span className={s.dim}>link: </span>
          <ExternalLink href={proj.link}>{proj.link}</ExternalLink>
        </div>
      ) : null}
    </div>
  );
}

function renderProjects(args: string[], lang: OsLang = "en"): ReactNode {
  const C = contentFor(lang);
  const slug = args[0]?.replace(/\.md$/, "");
  const projects = slug
    ? C.projects.filter((p) => slugify(p.name) === slug)
    : C.projects;

  if (slug && projects.length === 0) {
    return (
      <div className={s.block}>
        <span className={s.err}>no such project: {slug}</span>
      </div>
    );
  }

  return (
    <div className={s.block}>
      <Heading>{translate(lang, "// projects")}</Heading>
      {projects.map(renderProject)}
    </div>
  );
}

function renderSkills(lang: OsLang = "en"): ReactNode {
  const C = contentFor(lang);
  return (
    <div className={s.block}>
      <Heading>{translate(lang, "// skills")}</Heading>
      {C.skills.map((group) => (
        <div className={s.entry} key={group.label}>
          <div className={s.entryHead}>
            <span className={s.accent}>{group.label}</span>
          </div>
          <div className={s.tags}>
            {group.items.map((it) => (
              <span key={it} className={s.tag}>
                {it}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function renderEducation(lang: OsLang = "en"): ReactNode {
  const C = contentFor(lang);
  return (
    <div className={s.block}>
      <Heading>{translate(lang, "// education")}</Heading>
      {C.education.map((ed) => (
        <div className={s.entry} key={ed.credential}>
          <div className={s.entryHead}>
            <span className={s.bright}>{ed.credential}</span>
          </div>
          <div className={s.meta}>
            {ed.institution} · {ed.period}
          </div>
          {ed.detail ? <p className={s.para}>{ed.detail}</p> : null}
        </div>
      ))}
    </div>
  );
}

function renderContact(lang: OsLang = "en"): ReactNode {
  return (
    <div className={s.block}>
      <Heading>{translate(lang, "// contact")}</Heading>
      <div className={s.para}>
        <span className={s.dim}>email: </span>
        <ExternalLink href={`mailto:${content.email}`}>
          {content.email}
        </ExternalLink>
      </div>
      <div className={s.para}>
        <span className={s.dim}>based: </span>
        <span>{content.location}</span>
      </div>
      {content.links.map((l) => (
        <div className={s.para} key={l.label}>
          <span className={s.dim}>{l.label.toLowerCase()}: </span>
          <ExternalLink href={l.href}>{l.href}</ExternalLink>
        </div>
      ))}
      <p className={s.para}>
        <span className={s.dim}>tip: </span>
        <span>
          <code className={s.kbd}>open linkedin</code> /{" "}
          <code className={s.kbd}>open github</code> opens a link in a new tab.
        </span>
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* CV download                                                         */
/* ------------------------------------------------------------------ */

function downloadCv(): ReactNode {
  if (!content.cvFile) {
    return (
      <div className={s.block}>
        <span className={s.err}>cv: no file available.</span>
      </div>
    );
  }
  const a = document.createElement("a");
  a.href = content.cvFile;
  a.download = content.cvFile.split("/").pop() ?? "cv.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return (
    <div className={s.block}>
      <span className={s.accent}>↓</span> downloading{" "}
      <span className={s.bright}>{a.download}</span> …{" "}
      <span className={s.dim}>
        (no download? <ExternalLink href={content.cvFile}>open it here</ExternalLink>)
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* command registry                                                    */
/* ------------------------------------------------------------------ */

export const COMMANDS: Record<string, Command> = {
  help: {
    description: "list available commands",
    group: "system",
    run: (_args, ctx) => {
      const visible = Object.entries(COMMANDS).filter(([, c]) => !c.hidden);
      const L = ctx.lang;
      const core = visible.filter(([, c]) => c.group === "core");
      const rest = visible.filter(([, c]) => c.group !== "core");
      const grid = (rows: typeof visible) => (
        <div className={s.cmdGrid}>
          {rows.map(([name, c]) => (
            <div className={s.cmdRow} key={name}>
              <span className={s.accent}>{name}</span>
              <span className={s.dim}>{translate(L, c.description)}</span>
            </div>
          ))}
        </div>
      );
      return (
        <div className={s.block}>
          <Heading>{translate(L, "// the résumé")}</Heading>
          {L !== "en" ? (
            <p className={s.para}>
              {translate(L, "Type a command and press enter. Use tab to autocomplete, ↑/↓ for history.")}
            </p>
          ) : (
            <p className={s.para}>
              Type a command and press <code className={s.kbd}>enter</code>. Use{" "}
              <code className={s.kbd}>tab</code> to autocomplete,{" "}
              <code className={s.kbd}>↑/↓</code> for history.
            </p>
          )}
          {grid(core)}
          <div className={s.helpSep}>{translate(L, "── and to mess around ──────────────────────")}</div>
          {grid(rest)}
          <p className={s.para}>
            <span className={s.dim}>{translate(L, "…and a few hidden ones. 🥚")}</span>
          </p>
        </div>
      );
    },
  },

  about: { description: "who I am", group: "core", run: (_a, ctx) => renderAbout(ctx.lang) },

  experience: {
    description: "where I've worked",
    group: "core",
    aliases: ["experiences", "exp", "work"],
    run: (args, ctx) => renderExperience(args, ctx.lang),
  },

  projects: {
    description: "things I've built",
    group: "core",
    aliases: ["project"],
    run: (args, ctx) => renderProjects(args, ctx.lang),
  },

  skills: {
    description: "what I work with",
    group: "core",
    run: (_a, ctx) => renderSkills(ctx.lang),
  },

  education: {
    description: "where I studied",
    group: "core",
    aliases: ["edu"],
    run: (_a, ctx) => renderEducation(ctx.lang),
  },

  contact: {
    description: "how to reach me",
    group: "core",
    aliases: ["email"],
    run: (_a, ctx) => renderContact(ctx.lang),
  },

  cv: {
    description: "download my CV (PDF)",
    group: "core",
    aliases: ["resume"],
    run: () => downloadCv(),
  },

  /* ---- shell builtins ---- */

  ls: {
    description: "poke around the filesystem",
    usage: "ls [-a] [path]",
    aliases: ["dir"],
    run: (args, ctx) => {
      const showHidden = args.includes("-a") || args.includes("-la");
      const target = args.find((a) => !a.startsWith("-"));
      const dir = target
        ? resolvePath(ctx.cwd, target)?.node
        : dirAt(ctx.cwd);

      if (!dir || dir.type !== "dir") {
        return (
          <div className={s.block}>
            <span className={s.err}>ls: not a directory: {target}</span>
          </div>
        );
      }

      const names = Object.keys(dir.children)
        .filter((n) => showHidden || !n.startsWith("."))
        .sort();

      return (
        <div className={s.block}>
          <div className={s.lsGrid}>
            {names.map((n) => {
              const node = dir.children[n];
              const isDir = node.type === "dir";
              return (
                <span
                  key={n}
                  className={isDir ? s.lsDir : node.download ? s.accent : s.lsFile}
                >
                  {n}
                  {isDir ? "/" : ""}
                </span>
              );
            })}
          </div>
        </div>
      );
    },
  },

  cd: {
    description: "wander into a directory",
    usage: "cd [path]",
    run: (args, ctx) => {
      const target = args[0] ?? "/";
      const res = resolvePath(ctx.cwd, target);
      if (!res || res.node.type !== "dir") {
        return (
          <div className={s.block}>
            <span className={s.err}>cd: no such directory: {target}</span>
          </div>
        );
      }
      ctx.setCwd(res.path);
      return null;
    },
  },

  pwd: {
    description: "where am I again?",
    run: (_args, ctx) => (
      <div className={s.block}>{pathString(ctx.cwd)}</div>
    ),
  },

  cat: {
    description: "read a file out loud",
    usage: "cat <file>",
    run: (args, ctx) => {
      const target = args[0];
      if (!target) {
        return (
          <div className={s.block}>
            <span className={s.err}>cat: missing file operand</span>
          </div>
        );
      }
      const res = resolvePath(ctx.cwd, target);
      if (!res) {
        return (
          <div className={s.block}>
            <span className={s.err}>cat: no such file: {target}</span>
          </div>
        );
      }
      const node: FsNode = res.node;
      if (node.type === "dir") {
        return (
          <div className={s.block}>
            <span className={s.err}>cat: {target}: is a directory</span>
          </div>
        );
      }
      if (node.download) {
        return (
          <div className={s.block}>
            <span className={s.dim}>
              {target} is a binary PDF. run <code className={s.kbd}>cv</code> to
              download it.
            </span>
          </div>
        );
      }
      if (node.render) {
        const [cmd, ...rest] = node.render.split(" ");
        return COMMANDS[cmd]?.run(rest, ctx) ?? null;
      }
      return (
        <div className={s.block}>
          <pre className={s.fileText}>{node.text}</pre>
        </div>
      );
    },
  },

  clear: {
    description: "wipe the slate clean",
    aliases: ["cls"],
    run: (_args, ctx) => {
      ctx.clear();
      return null;
    },
  },

  theme: {
    description: "repaint the phosphor",
    usage: "theme [green|amber|blue|vcr]",
    run: (args, ctx) => {
      const name = args[0] as ThemeName | undefined;
      if (!name) {
        return (
          <div className={s.block}>
            <div className={s.para}>
              <span className={s.dim}>current: </span>
              <span className={s.accent}>{ctx.theme}</span>
            </div>
            <div className={s.para}>
              <span className={s.dim}>available: </span>
              {THEMES.join(", ")}
            </div>
          </div>
        );
      }
      if (!THEMES.includes(name)) {
        return (
          <div className={s.block}>
            <span className={s.err}>theme: unknown theme '{name}'</span>
            <span className={s.dim}> — try: {THEMES.join(", ")}</span>
          </div>
        );
      }
      ctx.setTheme(name);
      return (
        <div className={s.block}>
          theme set to <span className={s.accent}>{name}</span>.
        </div>
      );
    },
  },

  history: {
    description: "everything you've typed",
    run: (_args, ctx) => (
      <div className={s.block}>
        {ctx.history.length === 0 ? (
          <span className={s.dim}>no history yet.</span>
        ) : (
          ctx.history.map((h, i) => (
            <div key={i}>
              <span className={s.dim}>{String(i + 1).padStart(3, " ")} </span>
              {h}
            </div>
          ))
        )}
      </div>
    ),
  },

  open: {
    description: "launch a link in a new tab",
    usage: "open <linkedin|github|email|url>",
    run: (args) => {
      const arg = args[0];
      if (!arg) {
        return (
          <div className={s.block}>
            <span className={s.err}>open: what should I open?</span>
            <span className={s.dim}> — try: linkedin, github, email</span>
          </div>
        );
      }
      const named = content.links.find(
        (l) => l.label.toLowerCase() === arg.toLowerCase(),
      );
      const proj = content.projects.find(
        (p) => slugify(p.name) === slugify(arg) && p.link,
      );
      const href = named?.href ?? proj?.link ?? (/^https?:\/\//.test(arg) ? arg : null);
      if (!href) {
        return (
          <div className={s.block}>
            <span className={s.err}>open: don't know how to open '{arg}'</span>
          </div>
        );
      }
      window.open(href, "_blank", "noopener,noreferrer");
      return (
        <div className={s.block}>
          opening <ExternalLink href={href}>{href}</ExternalLink> …
        </div>
      );
    },
  },

  whoami: {
    description: "you tell me",
    run: () => (
      <div className={s.block}>
        <span className={s.bright}>{content.name}</span>
        <span className={s.dim}> — {content.tagline}</span>
      </div>
    ),
  },

  echo: {
    description: "I'll repeat after you",
    run: (args) => <div className={s.block}>{args.join(" ")}</div>,
  },

  man: {
    description: "the manual (yes, RTFM)",
    usage: "man <command>",
    run: (args) => {
      const name = args[0];
      const cmd = name ? resolve(name) : undefined;
      if (!cmd) {
        return (
          <div className={s.block}>
            <span className={s.err}>
              man: no manual entry for {name ?? "(nothing)"}
            </span>
          </div>
        );
      }
      return (
        <div className={s.block}>
          <div className={s.bright}>{name}</div>
          <p className={s.para}>{cmd.description}</p>
          {cmd.usage ? (
            <div className={s.para}>
              <span className={s.dim}>usage: </span>
              <code className={s.kbd}>{cmd.usage}</code>
            </div>
          ) : null}
          {cmd.aliases && cmd.aliases.length ? (
            <div className={s.para}>
              <span className={s.dim}>aliases: </span>
              {cmd.aliases.join(", ")}
            </div>
          ) : null}
        </div>
      );
    },
  },

  /* ---- easter eggs (hidden from help) ---- */

  sudo: {
    description: "",
    hidden: true,
    run: (args) => (
      <div className={s.block}>
        <span className={s.dim}>
          {content.name.split(" ")[0]} is not in the sudoers file. This incident
          will be reported. 🚨
        </span>
        {args.join(" ").includes("rm") ? (
          <div className={s.err}>(…and absolutely not that.)</div>
        ) : null}
      </div>
    ),
  },

  rm: {
    description: "",
    hidden: true,
    run: () => (
      <div className={s.block}>
        <span className={s.dim}>
          nice try. this filesystem is read-only — like my commitment to clean
          git history.
        </span>
      </div>
    ),
  },

  exit: {
    description: "end the session. (the résumé is not the whole machine.)",
    aliases: ["logout"],
    run: (_args, ctx) => {
      if (!ctx.exit) {
        return (
          <div className={s.block}>
            <span className={s.dim}>
              there is no exit. there is only more code. (close the tab, I
              guess.)
            </span>
          </div>
        );
      }
      // Exiting to the OS needs a desk-sized display; on phones the shell
      // refuses in-character. (Windowed exit just closes the window.)
      if (ctx.variant === "fullscreen" && displayTooSmallForOs()) {
        return (
          <div className={s.block}>
            <div className={s.err}>quartz: display incompatible</div>
            <p className={s.para}>
              <span className={s.dim}>
                SliceOS Quartz Layer requires an operator workstation (≥768px
                horizontal scan). This handheld display is not certified for
                desktop compositing.
              </span>
            </p>
            <p className={s.para}>
              <span className={s.dim}>
                Find a bigger screen, operator. The desktop will be waiting.
              </span>
            </p>
          </div>
        );
      }
      ctx.exit();
      return (
        <div className={s.block}>
          <div className={s.dim}>logout</div>
          <div>
            <span className={s.dim}>Connection to </span>
            <span className={s.bright}>laurel@portfolio</span>
            <span className={s.dim}> closed.</span>
          </div>
        </div>
      );
    },
  },

  sound: {
    description: "keyboard clicks on/off",
    usage: "sound [on|off]",
    run: (args) => {
      const arg = args[0]?.toLowerCase();
      if (arg === "on" || arg === "off") {
        setSoundEnabled(arg === "on");
        return (
          <div className={s.block}>
            sound <span className={s.accent}>{arg}</span>
            {arg === "on" ? " — clack away." : " — silence is golden."}
          </div>
        );
      }
      return (
        <div className={s.block}>
          <div className={s.para}>
            <span className={s.dim}>sound is </span>
            <span className={s.accent}>{soundEnabled() ? "on" : "off"}</span>
            <span className={s.dim}>
              {" "}
              — try <code className={s.kbd}>sound on</code> /{" "}
              <code className={s.kbd}>sound off</code>
            </span>
          </div>
        </div>
      );
    },
  },

  vim: {
    description: "",
    hidden: true,
    aliases: ["vi", "nano", "emacs"],
    run: () => (
      <div className={s.block}>
        <span className={s.dim}>
          opening editor… just kidding. you can never leave. type{" "}
          <code className={s.kbd}>help</code> instead.
        </span>
      </div>
    ),
  },

  coffee: {
    description: "",
    hidden: true,
    aliases: ["sl"],
    run: () => (
      <div className={s.block}>
        <span className={s.accent}>☕</span> brewing… HTTP 418: I'm a teapot.
      </div>
    ),
  },

  hello: {
    description: "",
    hidden: true,
    aliases: ["hi", "hey"],
    run: () => (
      <div className={s.block}>
        hey! 👋 type <code className={s.kbd}>help</code> to look around, or{" "}
        <code className={s.kbd}>cv</code> to grab my resume.
      </div>
    ),
  },
};

/* ------------------------------------------------------------------ */
/* lookup + completion helpers                                         */
/* ------------------------------------------------------------------ */

const ALIAS_MAP: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const [name, cmd] of Object.entries(COMMANDS)) {
    for (const alias of cmd.aliases ?? []) map[alias] = name;
  }
  return map;
})();

export function resolve(name: string): Command | undefined {
  return COMMANDS[name] ?? COMMANDS[ALIAS_MAP[name]];
}

/** Every name a user can type (canonical + aliases), minus hidden ones. */
export function completionNames(): string[] {
  const names: string[] = [];
  for (const [name, cmd] of Object.entries(COMMANDS)) {
    if (cmd.hidden) continue;
    names.push(name);
    for (const alias of cmd.aliases ?? []) names.push(alias);
  }
  return names.sort();
}

function editDistance(a: string, b: string): number {
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      );
    }
  }
  return dp[a.length][b.length];
}

/** Closest known command to a typo, or null if nothing is near enough. */
export function suggest(name: string): string | null {
  let best: string | null = null;
  let bestDist = Infinity;
  for (const candidate of completionNames()) {
    const d = editDistance(name, candidate);
    if (d < bestDist) {
      bestDist = d;
      best = candidate;
    }
  }
  // Only suggest when it's a plausible typo (≤ 2 edits, and not wildly long).
  const threshold = name.length <= 4 ? 1 : 2;
  return best && bestDist <= threshold ? best : null;
}
